import { SelectV2, SelectV2Option } from '@cimplico/components';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FieldReferenceMap } from './types';
import { isCWTSchemaField, isCWTSchemaTable } from '../store/utils';

type FieldSelectProps = {
    references: FieldReferenceMap[];
    onChange: (referenceMap?: FieldReferenceMap) => void;
};

function toOption(references: FieldReferenceMap[], default_?: string | null): SelectV2Option[] {
    let referenceMap: FieldReferenceMap | undefined;

    if (default_) {
        referenceMap = references.find((r) => r.ref === default_);
    }

    if (!referenceMap) {
        referenceMap = references[0];
    }

    if (!referenceMap) {
        return [];
    }

    return [
        {
            label: referenceMap.ref,
            value: referenceMap.ref,
            description: referenceMap.field.name,
        },
    ];
}

/**
 * Renders a select input for choosing a field to write a calculation for.
 *
 * @param references - a list of field references available for selection
 * @param onChange - a function that is called when the selected field reference changes
 */
function FieldSelect({ references, onChange }: FieldSelectProps): JSX.Element {
    const [query, setQuery] = useSearchParams();
    const queryReference = query.get('reference');
    const [value, setValue] = useState<SelectV2Option[]>(toOption(references, queryReference));
    const [search, setSearch] = useState<string>('');
    const reference = useMemo(() => references.find((r) => value.find((v) => v.value === r.ref)), [references, value]);

    function handleChange(v: SelectV2Option[]): void {
        setValue(v);
        const ref = references.find((r) => v.find((v) => v.value === r.ref));
        onChange(ref);

        if (ref) {
            query.set('reference', ref.ref);
        } else {
            query.delete('reference');
        }
        setQuery(query);
    }

    useEffect(() => {
        const ref = references.find((r) => value.find((v) => v.value === r.ref));
        onChange(ref);
    }, []);

    return (
        <div className="flex gap-4 relative w-fit">
            <SelectV2
                value={value}
                options={references
                    .filter((ref) => 'order' in ref.field && (isCWTSchemaField(ref.field) || isCWTSchemaTable(ref.field)))
                    .filter((r) => r.ref.toLowerCase().includes(search) || r.field.name.toLowerCase().includes(search))
                    .map((r) => ({ label: r.ref, value: r.ref, description: r.field.name }))}
                title="Field"
                description="Select the reference for the field to apply this calculation to."
                placeholder="Select a field"
                onChange={handleChange}
                onSearch={(q): void => setSearch(q.toLowerCase())}
            />
            {reference && (
                <div className="absolute left-[104%] bottom-2 flex items-center">
                    <ArrowRightIcon className="h-3.5 w-3.5 text-gray-500 shrink-0" />
                    <p className="text-sm text-gray-700 ml-4 shrink-0 capitalize">{reference?.field?.type?.toLowerCase()}</p>
                </div>
            )}
        </div>
    );
}

export default FieldSelect;
